// Selected-tier query param plumbing.
//
// Website pricing-card CTAs link to `${DASHBOARD_URL}/sign-up?plan=<plan>&interval=<interval>`
// (see apps/website/components/PricingTiers.tsx `ctaHref`). The signup-tier values
// are the three self-serve plans — `free`, `pro`, `team`; enterprise is a sales
// contact, never a signup link.
//
// Clerk's <SignUp> drops URL search params on its post-signup redirect, so the
// /sign-up route forwards the validated params onto `forceRedirectUrl`. The
// /dashboard route then reads them once and, for a paid plan, fires the same
// Stripe checkout flow the billing page uses.

import { z } from "zod";

// Self-serve plans a website pricing CTA can carry. Enterprise is excluded —
// its CTA is a mailto, never a signup link.
export const SELECTED_TIER_PLANS = ["free", "pro", "team"] as const;
export type SelectedTierPlan = (typeof SELECTED_TIER_PLANS)[number];

const planSchema = z.enum(SELECTED_TIER_PLANS);
const intervalSchema = z.enum(["month", "year"]);

// Search schema shared by /sign-up and /dashboard. Both fields optional —
// most visitors arrive without a selected tier (direct navigation, sign-in).
export const SELECTED_TIER_SEARCH_SCHEMA = z.object({
  plan: planSchema.optional(),
  interval: intervalSchema.optional(),
});

export type SelectedTierSearch = z.infer<typeof SELECTED_TIER_SEARCH_SCHEMA>;

// Paid plans require a Stripe checkout. `free` proceeds straight into the
// dashboard with no checkout.
export function isPaidSelectedTier(
  plan: SelectedTierPlan | undefined,
): plan is "pro" | "team" {
  return plan === "pro" || plan === "team";
}

// Post-signup redirect target handed to Clerk's <SignUp forceRedirectUrl>.
// Carries the selected tier onto /dashboard, the only channel that survives
// Clerk's multi-step signup flow.
export function selectedTierRedirectUrl(
  plan: SelectedTierPlan | undefined,
  interval: SelectedTierSearch["interval"],
): string {
  if (!plan) return "/dashboard";
  const params = new URLSearchParams({ plan });
  if (interval) params.set("interval", interval);
  return `/dashboard?${params.toString()}`;
}
